import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Key as KeyIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  CheckCircle as CheckCircleIcon,
  Insights as InsightsIcon,
  OpenInFull as OpenInFullIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { AdminKeyService } from '../../services/adminKeyService';

type MailerLiteAccount = {
  id: string;
  name: string;
  apiKey: string;
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

type MailerLiteGroup = {
  id: string;
  name: string;
};

type MailerLiteSubscriber = {
  id?: string;
  email: string;
  status?: string;
  subscribed_at?: string;
  created_at?: string;
  updated_at?: string;
  fields?: { [k: string]: any };
};

type UsageSummary = {
  days: number;
  since: string;
  accountCreatedAt?: string | null;
  cycleStart?: string | null;
  cycleEnd?: string | null;
  cycleLenDays?: number | null;
  resetsInDays?: number | null;
  subscribersTotal: number | null;
  campaignsSent: number;
  emailsSent: number;
  opens: number;
  clicks: number;
  unsubscribes: number;
  hardBounces: number;
  softBounces: number;
  notes?: string[];
};

type CampaignDraft = {
  id: string;
  accountId?: string;
  name: string;
  status?: 'draft' | 'published';
  mailerliteCampaignId?: string;
  subject: string;
  fromEmail: string;
  fromName: string;
  replyToEmail: string;
  groupId?: string;
  text: string;
  html: string;
  createdAt?: string;
  updatedAt?: string;
};

type MailerLiteState = {
  selectedId: string | null;
  accounts: MailerLiteAccount[];
};

type RightSection = 'api' | 'groups' | 'subscribers' | 'campaigns';

const emptyState: MailerLiteState = {
  selectedId: null,
  accounts: [],
};

const RIGHT_SECTION_KEY = 'ml_right_section_v1';

const createId = () => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  } catch {}
  return `ml_${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const maskKey = (key: string, show = 4) => {
  if (!key) return '';
  if (key.length <= show * 2) return key;
  return `${key.slice(0, show)}••••••${key.slice(-show)}`;
};

const MailerLiteTab: React.FC = () => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [showKey, setShowKey] = React.useState(false);
  const [state, setState] = React.useState<MailerLiteState>(emptyState);
  const [rightSection, setRightSection] = React.useState<RightSection>(() => {
    const saved = (localStorage.getItem(RIGHT_SECTION_KEY) || 'api') as RightSection;
    return saved === 'groups' || saved === 'subscribers' || saved === 'campaigns' || saved === 'api' ? saved : 'api';
  });

  const [groupsLoading, setGroupsLoading] = React.useState(false);
  const [groups, setGroups] = React.useState<MailerLiteGroup[]>([]);
  const [groupId, setGroupId] = React.useState<string>('');

  const [groupsManageOpen, setGroupsManageOpen] = React.useState(false);
  const [groupCreateName, setGroupCreateName] = React.useState('');
  const [groupCreateLoading, setGroupCreateLoading] = React.useState(false);
  const [groupDeleteLoadingId, setGroupDeleteLoadingId] = React.useState<string>('');

  const [subsLoading, setSubsLoading] = React.useState(false);
  const [subscribers, setSubscribers] = React.useState<MailerLiteSubscriber[]>([]);
  const [nextCursor, setNextCursor] = React.useState<string>('');

  const [perPage, setPerPage] = React.useState<number>(10);
  const [q, setQ] = React.useState<string>('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [dateFrom, setDateFrom] = React.useState<string>('');
  const [dateTo, setDateTo] = React.useState<string>('');

  const [addOpen, setAddOpen] = React.useState(false);
  const [addEmail, setAddEmail] = React.useState('');
  const [addFirstName, setAddFirstName] = React.useState('');
  const [addLastName, setAddLastName] = React.useState('');
  const [addCompany, setAddCompany] = React.useState('');
  const [addCountry, setAddCountry] = React.useState('');
  const [addCity, setAddCity] = React.useState('');
  const [addPhone, setAddPhone] = React.useState('');
  const [addState, setAddState] = React.useState('');
  const [addZip, setAddZip] = React.useState('');
  const [addGroups, setAddGroups] = React.useState<string[]>([]);
  const [addSaving, setAddSaving] = React.useState(false);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editId, setEditId] = React.useState<string | undefined>(undefined);
  const [editEmail, setEditEmail] = React.useState('');
  const [editFirstName, setEditFirstName] = React.useState('');
  const [editLastName, setEditLastName] = React.useState('');
  const [editCompany, setEditCompany] = React.useState('');
  const [editCountry, setEditCountry] = React.useState('');
  const [editCity, setEditCity] = React.useState('');
  const [editPhone, setEditPhone] = React.useState('');
  const [editState, setEditState] = React.useState('');
  const [editZip, setEditZip] = React.useState('');
  const [editGroups, setEditGroups] = React.useState<string[]>([]);
  const [editSaving, setEditSaving] = React.useState(false);

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteIdOrEmail, setDeleteIdOrEmail] = React.useState<string>('');
  const [deleteEmail, setDeleteEmail] = React.useState<string>('');
  const [deleteLoading, setDeleteLoading] = React.useState(false);

  const [campaignsLoading, setCampaignsLoading] = React.useState(false);
  const [campaignsSaving, setCampaignsSaving] = React.useState(false);
  const [campaignPublishing, setCampaignPublishing] = React.useState(false);
  const [campaignSending, setCampaignSending] = React.useState(false);
  const [campaignFieldErrors, setCampaignFieldErrors] = React.useState<Record<string, string>>({});
  const [sendOpen, setSendOpen] = React.useState(false);
  const [sendTarget, setSendTarget] = React.useState<'draft_group' | 'selected_subscribers'>('draft_group');
  const [sendSelectedEmails, setSendSelectedEmails] = React.useState<string[]>([]);
  const [sendSuccessOpen, setSendSuccessOpen] = React.useState(false);
  const [sendSuccessInfo, setSendSuccessInfo] = React.useState<{ campaignId?: string; scheduledAt?: string | null } | null>(null);
  const [usageOpen, setUsageOpen] = React.useState(false);
  const [usageAccountId, setUsageAccountId] = React.useState<string>('');
  const [usageDays, setUsageDays] = React.useState<number>(30);
  const [usageLoading, setUsageLoading] = React.useState(false);
  const [usageData, setUsageData] = React.useState<UsageSummary | null>(null);
  const [accountUsage, setAccountUsage] = React.useState<Record<string, UsageSummary | null>>({});
  const [campaignDrafts, setCampaignDrafts] = React.useState<Array<{ id: string; name: string; subject: string; updatedAt?: string | null; createdAt?: string | null; accountId?: string | null }>>([]);
  const [campaignSelectedId, setCampaignSelectedId] = React.useState<string | null>(null);
  const [campaignDraft, setCampaignDraft] = React.useState<CampaignDraft | null>(null);
  const [campaignTab, setCampaignTab] = React.useState<'html' | 'text' | 'preview'>('html');
  const autosaveTimerRef = React.useRef<number | null>(null);

  const [workspaceOpen, setWorkspaceOpen] = React.useState(false);

  const selected = React.useMemo(() => {
    if (!state.selectedId) return null;
    return state.accounts.find((a) => a.id === state.selectedId) || null;
  }, [state.accounts, state.selectedId]);

  const sendSuccessWhenText = React.useMemo(() => {
    const v = sendSuccessInfo?.scheduledAt;
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  }, [sendSuccessInfo?.scheduledAt]);

  const callMailerLiteApi = React.useCallback(async (action: 'save' | 'load', data?: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const res = await fetch('/api/mailerlite.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, ...(action === 'save' ? { data } : {}) }),
    });

    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      throw new Error(json?.error || `MailerLite API error (${res.status})`);
    }

    return json;
  }, []);

  const callCampaignsApi = React.useCallback(async (action: 'list' | 'load' | 'save' | 'delete', payload?: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const res = await fetch('/api/mail-campaigns.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, ...(payload || {}) }),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      throw new Error(json?.error || `Campaigns API error (${res.status})`);
    }
    return json;
  }, []);

  const callMailerLiteProxy = React.useCallback(async (action: string, payload?: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');

    const accountId = state.selectedId;
    if (!accountId) throw new Error('Select a MailerLite account first');

    const res = await fetch('/api/mailerlite-api.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, accountId, ...(payload || {}) }),
    });

    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      const msg = json?.error || `MailerLite API error (${res.status})`;
      const err: any = new Error(msg);
      err.details = json?.details;
      throw err;
    }
    return json.data;
  }, [state.selectedId]);

  const callMailerLiteProxyForAccount = React.useCallback(async (accountId: string, action: string, payload?: any) => {
    const token = await AdminKeyService.getSecret();
    if (!token) throw new Error('Missing admin token');
    if (!accountId) throw new Error('Missing accountId');

    const res = await fetch('/api/mailerlite-api.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Auth-Token': token },
      body: JSON.stringify({ action, accountId, ...(payload || {}) }),
    });
    const json = await res.json().catch(() => null);
    if (!res.ok || !json?.ok) {
      const msg = json?.error || `MailerLite API error (${res.status})`;
      const err: any = new Error(msg);
      err.details = json?.details;
      throw err;
    }
    return json.data;
  }, []);

  const openUsage = React.useCallback((accountId: string) => {
    setUsageAccountId(accountId);
    setUsageDays(30);
    setUsageData(null);
    setUsageOpen(true);
  }, []);

  const loadUsage = React.useCallback(async (days: number, accountId: string) => {
    if (!accountId) return;
    try {
      setUsageLoading(true);
      const data = await callMailerLiteProxy('usage_summary', { days, accountId });
      setUsageData(data as UsageSummary);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load usage');
    } finally {
      setUsageLoading(false);
    }
  }, [callMailerLiteProxy]);

  React.useEffect(() => {
    if (!usageOpen) return;
    if (!usageAccountId) return;
    loadUsage(usageDays, usageAccountId);
  }, [usageDays, usageAccountId, usageOpen, loadUsage]);

  const loadAccountUsageSummaries = React.useCallback(async () => {
    const accounts = state.accounts;
    if (!accounts.length) {
      setAccountUsage({});
      return;
    }

    try {
      const entries = await Promise.all(
        accounts.map(async (a) => {
          try {
            const data = await callMailerLiteProxyForAccount(a.id, 'usage_summary', { mode: 'billing_cycle', createdAtHint: a.createdAt || '' });
            return [a.id, data as UsageSummary] as const;
          } catch {
            return [a.id, null] as const;
          }
        })
      );
      const next: Record<string, UsageSummary | null> = {};
      entries.forEach(([id, d]) => { next[id] = d; });
      setAccountUsage(next);
    } catch {
      // keep silent; per-account failures handled above
    }
  }, [callMailerLiteProxyForAccount, state.accounts]);

  React.useEffect(() => {
    loadAccountUsageSummaries();
  }, [loadAccountUsageSummaries]);

  const mapMailerLiteErrorsToFields = React.useCallback((details: any) => {
    const errs: Record<string, string> = {};
    const raw = details?.details?.details || details?.details || details;
    const apiErrors = raw?.errors && typeof raw.errors === 'object' ? raw.errors : null;
    if (!apiErrors) return errs;

    const add = (field: string, msg: string) => {
      if (!field || !msg) return;
      if (errs[field]) return;
      errs[field] = msg;
    };

    Object.entries(apiErrors).forEach(([k, v]) => {
      const msg = Array.isArray(v) ? String(v[0] ?? '') : String(v ?? '');
      const key = String(k);

      if (key === 'name' || key.startsWith('name.')) add('name', msg);
      else if (key.includes('subject')) add('subject', msg);
      else if (key.includes('from_name')) add('fromName', msg);
      else if (key.includes('reply_to')) add('replyToEmail', msg);
      else if (key === 'from' || key.endsWith('.from') || key.includes('.from')) add('fromEmail', msg);
      else if (key.includes('filter')) add('groupId', msg);
      else if (key.includes('plain_text')) add('text', msg);
      else if (key.includes('html')) add('html', msg);
      else if (key.startsWith('emails.0') || key === 'emails') add('publish', msg);
      else add('publish', `${key}: ${msg}`);
    });

    return errs;
  }, []);

  const resetSubscribers = React.useCallback(() => {
    setSubscribers([]);
    setNextCursor('');
  }, []);

  const fmtWhen = (iso?: string | null) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      const json = await callMailerLiteApi('load');
      const data = json?.data;
      if (!data || typeof data !== 'object') {
        setState(emptyState);
        return;
      }

      const accounts: MailerLiteAccount[] = Array.isArray((data as any).accounts) ? (data as any).accounts : [];
      const selectedId: string | null = typeof (data as any).selectedId === 'string' ? (data as any).selectedId : null;
      const normalized: MailerLiteAccount[] = accounts
        .filter((a: any) => a && typeof a === 'object')
        .map((a: any) => ({
          id: typeof a.id === 'string' ? a.id : createId(),
          name: typeof a.name === 'string' ? a.name : 'MailerLite',
          apiKey: typeof a.apiKey === 'string' ? a.apiKey : '',
          isDefault: Boolean(a.isDefault),
          createdAt: typeof a.createdAt === 'string' ? a.createdAt : undefined,
          updatedAt: typeof a.updatedAt === 'string' ? a.updatedAt : undefined,
        }));

      const fallbackSelected = selectedId && normalized.some((a) => a.id === selectedId)
        ? selectedId
        : (normalized.find((a) => a.isDefault)?.id || normalized[0]?.id || null);

      setState({ accounts: normalized, selectedId: fallbackSelected });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load MailerLite settings');
    } finally {
      setLoading(false);
    }
  }, [callMailerLiteApi]);

  const save = React.useCallback(async () => {
    try {
      setSaving(true);
      await callMailerLiteApi('save', state);
      toast.success('MailerLite accounts saved');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save MailerLite settings');
    } finally {
      setSaving(false);
    }
  }, [callMailerLiteApi, state]);

  React.useEffect(() => {
    load();
  }, [load]);

  const loadGroups = React.useCallback(async () => {
    try {
      setGroupsLoading(true);
      const data = await callMailerLiteProxy('groups_list', { page: 1 });
      const list = Array.isArray(data?.data) ? data.data : [];
      const normalized: MailerLiteGroup[] = list
        .filter((g: any) => g && typeof g === 'object')
        .map((g: any) => ({ id: String(g.id), name: String(g.name || 'Group') }));
      setGroups(normalized);
      setGroupId((prev) => {
        if (!prev) return '';
        if (!normalized.some((g) => g.id === prev)) return '';
        return prev;
      });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load groups');
    } finally {
      setGroupsLoading(false);
    }
  }, [callMailerLiteProxy]);

  const loadSubscribers = React.useCallback(async (cursor?: string, opts?: { reset?: boolean }) => {
    try {
      setSubsLoading(true);
      const action = groupId ? 'group_subscribers_list' : 'subscribers_list';
      const payload: any = { limit: perPage, cursor: cursor || '' };
      if (groupId) payload.groupId = groupId;
      const data = await callMailerLiteProxy(action, payload);
      const list = Array.isArray(data?.data) ? data.data : [];
      const normalized: MailerLiteSubscriber[] = list
        .filter((s: any) => s && typeof s === 'object')
        .map((s: any) => ({
          id: s.id ? String(s.id) : undefined,
          email: String(s.email || ''),
          status: s.status ? String(s.status) : undefined,
          subscribed_at: s.subscribed_at ? String(s.subscribed_at) : undefined,
          created_at: s.created_at ? String(s.created_at) : undefined,
          updated_at: s.updated_at ? String(s.updated_at) : undefined,
          fields: s.fields && typeof s.fields === 'object' ? s.fields : undefined,
        }))
        .filter((s: MailerLiteSubscriber) => Boolean(s.email));

      const nc = data?.meta?.next_cursor ? String(data.meta.next_cursor) : '';
      setNextCursor(nc);
      setSubscribers((prev) => {
        if (opts?.reset) return normalized;
        return cursor ? [...prev, ...normalized] : normalized;
      });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load subscribers');
    } finally {
      setSubsLoading(false);
    }
  }, [callMailerLiteProxy, groupId, perPage]);

  const fetchAllSubscribers = React.useCallback(async () => {
    const accountId = state.selectedId;
    if (!accountId) return;

    const activeGroupId = groupId;
    const batchSize = Math.max(10, Math.min(100, perPage));
    const action = activeGroupId ? 'group_subscribers_list' : 'subscribers_list';

    try {
      setSubsLoading(true);
      setNextCursor('');
      setSubscribers([]);

      let cursor = '';
      let all: MailerLiteSubscriber[] = [];
      let loops = 0;

      while (true) {
        loops += 1;
        if (loops > 200) {
          throw new Error('Stopped after 200 pages to avoid runaway fetch. Narrow your list or increase per-page.');
        }
        if (state.selectedId !== accountId) {
          throw new Error('Fetch cancelled because selected account changed');
        }

        const payload: any = { limit: batchSize, cursor };
        if (activeGroupId) payload.groupId = activeGroupId;
        const data = await callMailerLiteProxy(action, payload);

        const list = Array.isArray(data?.data) ? data.data : [];
        const normalized: MailerLiteSubscriber[] = list
          .filter((s: any) => s && typeof s === 'object')
          .map((s: any) => ({
            id: s.id ? String(s.id) : undefined,
            email: String(s.email || ''),
            status: s.status ? String(s.status) : undefined,
            subscribed_at: s.subscribed_at ? String(s.subscribed_at) : undefined,
            created_at: s.created_at ? String(s.created_at) : undefined,
            updated_at: s.updated_at ? String(s.updated_at) : undefined,
            fields: s.fields && typeof s.fields === 'object' ? s.fields : undefined,
          }))
          .filter((s: MailerLiteSubscriber) => Boolean(s.email));

        all = all.concat(normalized);
        setSubscribers([...all]);

        const nc = data?.meta?.next_cursor ? String(data.meta.next_cursor) : '';
        cursor = nc;
        if (!cursor) break;
      }

      setNextCursor('');
      toast.success(`Fetched ${all.length} subscribers`);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to fetch all subscribers');
    } finally {
      setSubsLoading(false);
    }
  }, [callMailerLiteProxy, groupId, perPage, state.selectedId]);

  React.useEffect(() => {
    if (!state.selectedId) return;
    resetSubscribers();
    setGroups([]);
    setGroupId('');
    setTimeout(() => {
      loadGroups();
    }, 0);
  }, [state.selectedId, loadGroups, resetSubscribers]);

  React.useEffect(() => {
    if (!state.selectedId) return;
    resetSubscribers();
    loadSubscribers('', { reset: true });
  }, [state.selectedId, groupId, perPage, loadSubscribers, resetSubscribers]);

  const loadCampaignsList = React.useCallback(async () => {
    try {
      setCampaignsLoading(true);
      const json = await callCampaignsApi('list');
      const items = Array.isArray(json?.items) ? json.items : [];
      const filtered = items.filter((x: any) => !state.selectedId || x?.accountId === state.selectedId || !x?.accountId);
      setCampaignDrafts(filtered);
      if (!campaignSelectedId && filtered.length > 0) {
        setCampaignSelectedId(String(filtered[0].id));
      }
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load campaign drafts');
    } finally {
      setCampaignsLoading(false);
    }
  }, [callCampaignsApi, campaignSelectedId, state.selectedId]);

  const loadCampaignDraft = React.useCallback(async (id: string) => {
    try {
      setCampaignsLoading(true);
      const json = await callCampaignsApi('load', { id });
      const d = json?.draft;
      if (!d || typeof d !== 'object') throw new Error('Draft not found');
      const normalized: CampaignDraft = {
        id: String(d.id || id),
        accountId: typeof d.accountId === 'string' ? d.accountId : state.selectedId || undefined,
        name: typeof d.name === 'string' ? d.name : 'Campaign',
        status: d.status === 'published' ? 'published' : 'draft',
        mailerliteCampaignId: typeof d.mailerliteCampaignId === 'string' ? d.mailerliteCampaignId : undefined,
        subject: typeof d.subject === 'string' ? d.subject : '',
        fromEmail: typeof d.fromEmail === 'string' ? d.fromEmail : '',
        fromName: typeof d.fromName === 'string' ? d.fromName : '',
        replyToEmail: typeof d.replyToEmail === 'string' ? d.replyToEmail : '',
        groupId: typeof d.groupId === 'string' ? d.groupId : undefined,
        text: typeof d.text === 'string' ? d.text : '',
        html: typeof d.html === 'string' ? d.html : '',
        createdAt: typeof d.createdAt === 'string' ? d.createdAt : undefined,
        updatedAt: typeof d.updatedAt === 'string' ? d.updatedAt : undefined,
      };
      setCampaignDraft(normalized);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to load draft');
    } finally {
      setCampaignsLoading(false);
    }
  }, [callCampaignsApi, state.selectedId]);

  const saveCampaignDraft = React.useCallback(async (draft: CampaignDraft) => {
    try {
      setCampaignsSaving(true);
      await callCampaignsApi('save', { draft });
      await loadCampaignsList();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save draft');
    } finally {
      setCampaignsSaving(false);
    }
  }, [callCampaignsApi, loadCampaignsList]);

  const publishCampaignDraft = React.useCallback(async () => {
    if (!campaignDraft) throw new Error('Select a draft first');
    if (!state.selectedId) throw new Error('Select a MailerLite account first');
    if ((campaignDraft.accountId || state.selectedId) !== state.selectedId) {
      throw new Error('Draft account does not match selected MailerLite account');
    }

    try {
      setCampaignPublishing(true);
      setCampaignFieldErrors({});
      const data = await callMailerLiteProxy('campaign_upsert', { draft: campaignDraft });
      const remoteId = data?.campaignId ? String(data.campaignId) : '';
      if (!remoteId) throw new Error('MailerLite did not return a campaign id');

      const next: CampaignDraft = {
        ...campaignDraft,
        mailerliteCampaignId: remoteId,
        status: 'published',
      };
      setCampaignDraft(next);
      await saveCampaignDraft(next);
      toast.success('Published to MailerLite');
    } catch (e: any) {
      const mapped = mapMailerLiteErrorsToFields(e?.details);
      if (mapped && Object.keys(mapped).length > 0) {
        setCampaignFieldErrors(mapped);
      }
      toast.error(e?.message || 'Failed to publish to MailerLite');
    } finally {
      setCampaignPublishing(false);
    }
  }, [callMailerLiteProxy, campaignDraft, saveCampaignDraft, state.selectedId]);

  const openSendDialog = React.useCallback(() => {
    if (!campaignDraft) {
      toast.error('Select a draft first');
      return;
    }
    if (!campaignDraft.mailerliteCampaignId) {
      toast.error('Publish the draft first');
      return;
    }
    setSendTarget('draft_group');
    setSendSelectedEmails([]);
    setSendOpen(true);
  }, [campaignDraft]);

  const sendCampaign = React.useCallback(async () => {
    if (!campaignDraft) throw new Error('Select a draft first');
    const campaignId = campaignDraft.mailerliteCampaignId ? String(campaignDraft.mailerliteCampaignId) : '';
    if (!campaignId) throw new Error('Publish the draft first');

    const payload: any = {
      campaignId,
      delivery: 'instant',
      draft: campaignDraft,
    };

    if (sendTarget === 'draft_group') {
      if (!campaignDraft.groupId) throw new Error('Select a target group');
      payload.groupId = String(campaignDraft.groupId);
    } else {
      if (!sendSelectedEmails || sendSelectedEmails.length === 0) {
        throw new Error('Select at least one subscriber');
      }
      payload.subscriberEmails = sendSelectedEmails;
    }

    try {
      setCampaignSending(true);
      const res = await callMailerLiteProxy('campaign_send', payload);
      const scheduledAt =
        (res?.campaign?.scheduled_at as string | undefined) ||
        (res?.campaign?.scheduledAt as string | undefined) ||
        (res?.campaign?.send_at as string | undefined) ||
        (res?.campaign?.sendAt as string | undefined) ||
        (res?.campaign?.delivery_at as string | undefined) ||
        (res?.campaign?.deliveryAt as string | undefined) ||
        null;
      setSendSuccessInfo({ campaignId, scheduledAt });
      setSendSuccessOpen(true);
      setSendOpen(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to send campaign');
    } finally {
      setCampaignSending(false);
    }
  }, [callMailerLiteProxy, campaignDraft, sendSelectedEmails, sendTarget]);

  const scheduleAutosave = React.useCallback((next: CampaignDraft) => {
    if (autosaveTimerRef.current) {
      window.clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = window.setTimeout(() => {
      saveCampaignDraft(next);
    }, 800);
  }, [saveCampaignDraft]);

  const updateCampaignDraft = (patch: Partial<CampaignDraft>) => {
    setCampaignDraft((prev) => {
      if (!prev) return prev;
      const next = { ...prev, ...patch, accountId: prev.accountId || state.selectedId || undefined };
      scheduleAutosave(next);
      return next;
    });

    const keys = Object.keys(patch);
    if (keys.length > 0) {
      setCampaignFieldErrors((prev) => {
        if (!prev || Object.keys(prev).length === 0) return prev;
        const next = { ...prev };
        keys.forEach((k) => {
          if (k in next) delete next[k];
        });
        if ('groupId' in patch && next.groupId) delete next.groupId;
        if ('html' in patch && next.html) delete next.html;
        if ('text' in patch && next.text) delete next.text;
        if (keys.some((k) => k === 'fromEmail' || k === 'replyToEmail')) {
          // clear any generic publish error once user edits the sender fields
          if (next.publish) delete next.publish;
        }
        return next;
      });
    }
  };

  const createNewDraft = async () => {
    const id = createId();
    const nowName = `Campaign ${new Date().toLocaleDateString()}`;
    const d: CampaignDraft = {
      id,
      accountId: state.selectedId || undefined,
      name: nowName,
      status: 'draft',
      subject: '',
      fromEmail: '',
      fromName: '',
      replyToEmail: '',
      groupId: groupId || undefined,
      text: '',
      html: '<div style="font-family:Arial,sans-serif">\n  <h2>Hello!</h2>\n  <p>Write your campaign here.</p>\n</div>',
    };
    setCampaignSelectedId(id);
    setCampaignDraft(d);
    await saveCampaignDraft(d);
    toast.success('Draft created');
  };

  const deleteDraft = async (id: string) => {
    try {
      setCampaignsSaving(true);
      await callCampaignsApi('delete', { id });
      if (campaignSelectedId === id) {
        setCampaignSelectedId(null);
        setCampaignDraft(null);
      }
      await loadCampaignsList();
      toast.success('Draft deleted');
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete draft');
    } finally {
      setCampaignsSaving(false);
    }
  };

  React.useEffect(() => {
    if (!state.selectedId) return;
    loadCampaignsList();
  }, [state.selectedId, loadCampaignsList]);

  React.useEffect(() => {
    if (!campaignSelectedId) return;
    loadCampaignDraft(campaignSelectedId);
  }, [campaignSelectedId, loadCampaignDraft]);

  const openAdd = () => {
    setAddEmail('');
    setAddFirstName('');
    setAddLastName('');
    setAddCompany('');
    setAddCountry('');
    setAddCity('');
    setAddPhone('');
    setAddState('');
    setAddZip('');
    setAddGroups(groupId ? [groupId] : []);
    setAddOpen(true);
  };

  const openEdit = (s: MailerLiteSubscriber) => {
    setEditId(s.id);
    setEditEmail(s.email);
    setEditFirstName(String(s.fields?.name || ''));
    setEditLastName(String(s.fields?.last_name || ''));
    setEditCompany(String(s.fields?.company || ''));
    setEditCountry(String(s.fields?.country || ''));
    setEditCity(String(s.fields?.city || ''));
    setEditPhone(String(s.fields?.phone || ''));
    setEditState(String(s.fields?.state || ''));
    setEditZip(String(s.fields?.z_i_p || ''));
    setEditGroups(groupId ? [groupId] : []);
    setEditOpen(true);
  };

  const createGroup = React.useCallback(async () => {
    try {
      const name = groupCreateName.trim();
      if (!name) throw new Error('Group name is required');
      setGroupCreateLoading(true);
      await callMailerLiteProxy('group_create', { name });
      setGroupCreateName('');
      toast.success('Group created');
      await loadGroups();
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create group');
    } finally {
      setGroupCreateLoading(false);
    }
  }, [callMailerLiteProxy, groupCreateName, loadGroups]);

  const deleteGroup = React.useCallback(async (gid: string) => {
    try {
      if (!gid) throw new Error('Missing group id');
      setGroupDeleteLoadingId(gid);
      await callMailerLiteProxy('group_delete', { groupId: gid });
      toast.success('Group deleted');
      await loadGroups();
      setGroupId((prev) => (prev === gid ? '' : prev));
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete group');
    } finally {
      setGroupDeleteLoadingId('');
    }
  }, [callMailerLiteProxy, loadGroups]);

  const openDelete = (s: MailerLiteSubscriber) => {
    setDeleteIdOrEmail(s.id || s.email);
    setDeleteEmail(s.email);
    setDeleteOpen(true);
  };

  const saveSubscriber = React.useCallback(async () => {
    try {
      setAddSaving(true);
      const email = addEmail.trim();
      if (!email) throw new Error('Email is required');
      const fields: any = {};
      fields.name = addFirstName.trim() ? addFirstName.trim() : null;
      fields.last_name = addLastName.trim() ? addLastName.trim() : null;
      fields.company = addCompany.trim() ? addCompany.trim() : null;
      fields.country = addCountry.trim() ? addCountry.trim() : null;
      fields.city = addCity.trim() ? addCity.trim() : null;
      fields.phone = addPhone.trim() ? addPhone.trim() : null;
      fields.state = addState.trim() ? addState.trim() : null;
      fields.z_i_p = addZip.trim() ? addZip.trim() : null;
      const groupsToAssign = Array.isArray(addGroups) ? addGroups.filter(Boolean) : [];
      await callMailerLiteProxy('subscriber_upsert', { email, fields, groups: groupsToAssign });
      toast.success('Subscriber saved');
      setAddOpen(false);
      resetSubscribers();
      loadSubscribers('', { reset: true });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save subscriber');
    } finally {
      setAddSaving(false);
    }
  }, [addCity, addCompany, addCountry, addEmail, addFirstName, addGroups, addLastName, addPhone, addState, addZip, callMailerLiteProxy, loadSubscribers, resetSubscribers]);

  const saveSubscriberEdit = React.useCallback(async () => {
    try {
      setEditSaving(true);
      const email = editEmail.trim();
      if (!email) throw new Error('Email is required');
      const fields: any = {};
      fields.name = editFirstName.trim() ? editFirstName.trim() : null;
      fields.last_name = editLastName.trim() ? editLastName.trim() : null;
      fields.company = editCompany.trim() ? editCompany.trim() : null;
      fields.country = editCountry.trim() ? editCountry.trim() : null;
      fields.city = editCity.trim() ? editCity.trim() : null;
      fields.phone = editPhone.trim() ? editPhone.trim() : null;
      fields.state = editState.trim() ? editState.trim() : null;
      fields.z_i_p = editZip.trim() ? editZip.trim() : null;
      const groupsToAssign = Array.isArray(editGroups) ? editGroups.filter(Boolean) : [];
      await callMailerLiteProxy('subscriber_upsert', { email, fields, groups: groupsToAssign });
      toast.success('Subscriber updated');
      setEditOpen(false);
      resetSubscribers();
      loadSubscribers('', { reset: true });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to update subscriber');
    } finally {
      setEditSaving(false);
    }
  }, [callMailerLiteProxy, editCity, editCompany, editCountry, editEmail, editFirstName, editGroups, editLastName, editPhone, editState, editZip, loadSubscribers, resetSubscribers]);

  const deleteSubscriber = React.useCallback(async () => {
    try {
      setDeleteLoading(true);
      if (!deleteIdOrEmail) throw new Error('Missing subscriber identifier');
      await callMailerLiteProxy('subscriber_delete', { identifier: deleteIdOrEmail });
      toast.success('Subscriber deleted');
      setDeleteOpen(false);
      resetSubscribers();
      loadSubscribers('', { reset: true });
    } catch (e: any) {
      toast.error(e?.message || 'Failed to delete subscriber');
    } finally {
      setDeleteLoading(false);
    }
  }, [callMailerLiteProxy, deleteIdOrEmail, loadSubscribers, resetSubscribers]);

  const filteredSubscribers = React.useMemo(() => {
    const qq = q.trim().toLowerCase();
    const fromTs = dateFrom ? Date.parse(dateFrom + 'T00:00:00') : NaN;
    const toTs = dateTo ? Date.parse(dateTo + 'T23:59:59') : NaN;
    return subscribers.filter((s) => {
      if (statusFilter !== 'all' && (s.status || '').toLowerCase() !== statusFilter) return false;
      if (qq) {
        const name = String((s.fields?.name || '') + ' ' + (s.fields?.last_name || '')).toLowerCase();
        if (!s.email.toLowerCase().includes(qq) && !name.includes(qq)) return false;
      }
      if (dateFrom || dateTo) {
        const raw = s.subscribed_at || s.created_at || '';
        const ts = raw ? Date.parse(raw.replace(' ', 'T')) : NaN;
        if (!Number.isNaN(fromTs) && !Number.isNaN(ts) && ts < fromTs) return false;
        if (!Number.isNaN(toTs) && !Number.isNaN(ts) && ts > toTs) return false;
      }
      return true;
    });
  }, [subscribers, q, statusFilter, dateFrom, dateTo]);

  const setSelected = (id: string) => {
    setState((p) => ({ ...p, selectedId: id }));
  };

  const addAccount = () => {
    const now = new Date().toISOString();
    const id = createId();
    const next: MailerLiteAccount = { id, name: `MailerLite ${state.accounts.length + 1}`, apiKey: '', isDefault: state.accounts.length === 0, createdAt: now, updatedAt: now };
    setState((p) => ({ ...p, accounts: [next, ...p.accounts], selectedId: id }));
  };

  const deleteAccount = (id: string) => {
    setState((p) => {
      const nextAccounts = p.accounts.filter((a) => a.id !== id);
      const nextSelected = p.selectedId === id ? (nextAccounts[0]?.id || null) : p.selectedId;
      return { ...p, accounts: nextAccounts, selectedId: nextSelected };
    });
  };

  const setDefault = (id: string) => {
    setState((p) => ({
      ...p,
      accounts: p.accounts.map((a) => ({ ...a, isDefault: a.id === id })),
      selectedId: id,
    }));
  };

  const updateSelected = (patch: Partial<MailerLiteAccount>) => {
    if (!selected) return;
    const now = new Date().toISOString();
    setState((p) => ({
      ...p,
      accounts: p.accounts.map((a) => (a.id === selected.id ? { ...a, ...patch, updatedAt: now } : a)),
    }));
  };

  const onRightSectionChange = (_: React.SyntheticEvent, v: RightSection) => {
    setRightSection(v);
    localStorage.setItem(RIGHT_SECTION_KEY, v);
  };

  const openWorkspace = React.useCallback((section?: RightSection) => {
    if (section) {
      setRightSection(section);
      localStorage.setItem(RIGHT_SECTION_KEY, section);
    }
    setWorkspaceOpen(true);
  }, []);

  const renderRightSectionContent = React.useCallback((section: RightSection) => {
    if (!selected) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography fontWeight={800} gutterBottom>
            Select an account
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create an account on the left, then paste your MailerLite API key here.
          </Typography>
        </Box>
      );
    }

    if (section === 'api') {
      return (
        <Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 2,
            }}
          >
            <Box>
              <TextField
                fullWidth
                label="Account Name"
                value={selected.name}
                onChange={(e) => updateSelected({ name: e.target.value })}
              />
            </Box>
            <Box>
              <TextField
                fullWidth
                label="Default"
                value={selected.isDefault ? 'Yes' : 'No'}
                InputProps={{ readOnly: true }}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}>
              <TextField
                fullWidth
                label="MailerLite API Key"
                value={selected.apiKey}
                onChange={(e) => updateSelected({ apiKey: e.target.value })}
                type={showKey ? 'text' : 'password'}
                placeholder="Paste your API key"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowKey((p) => !p)} edge="end">
                        {showKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            Tip: keep one default account. Later we can add a “Test API Key” button and MailerLite campaign features.
          </Typography>
        </Box>
      );
    }

    if (section === 'groups') {
      return (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CampaignIcon fontSize="small" />
              <Typography fontWeight={900}>Groups</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small"
                label="New group"
                value={groupCreateName}
                onChange={(e) => setGroupCreateName(e.target.value)}
                disabled={groupsLoading || groupCreateLoading}
              />
              <Button
                variant="contained"
                onClick={createGroup}
                disabled={groupsLoading || groupCreateLoading}
                startIcon={groupCreateLoading ? <CircularProgress size={16} /> : <AddIcon />}
              >
                Create
              </Button>
              <Button
                variant="outlined"
                onClick={loadGroups}
                disabled={groupsLoading}
                startIcon={groupsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              >
                Refresh
              </Button>
            </Box>
          </Box>

          <Divider />

          <Box sx={{ p: 1.5 }}>
            {groups.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                No groups yet. Create one above.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {groups.map((g) => {
                  const selectedChip = g.id === groupId;
                  return (
                    <Chip
                      key={g.id}
                      label={g.name}
                      color={selectedChip ? 'primary' : 'default'}
                      variant={selectedChip ? 'filled' : 'outlined'}
                      onClick={() => setGroupId(g.id)}
                      onDelete={groupDeleteLoadingId === g.id ? undefined : () => deleteGroup(g.id)}
                      deleteIcon={groupDeleteLoadingId === g.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                      sx={{ fontWeight: 800 }}
                    />
                  );
                })}
              </Box>
            )}
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Selected group is used as the subscriber list filter and as the default group when adding/editing subscribers.
              </Typography>
            </Box>
          </Box>
        </Paper>
      );
    }

    if (section === 'subscribers') {
      return (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon fontSize="small" />
              <Typography fontWeight={900}>Subscribers</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 240 }} disabled={groupsLoading || subsLoading}>
                <InputLabel id="ml-group">Group</InputLabel>
                <Select
                  labelId="ml-group"
                  label="Group"
                  value={groupId}
                  onChange={(e) => setGroupId(String(e.target.value))}
                >
                  {groups.map((g) => (
                    <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 140 }} disabled={groupsLoading || subsLoading}>
                <InputLabel id="ml-per-page">Per page</InputLabel>
                <Select
                  labelId="ml-per-page"
                  label="Per page"
                  value={String(perPage)}
                  onChange={(e) => setPerPage(parseInt(String(e.target.value), 10) || 10)}
                >
                  {[10, 25, 50, 100].map((n) => (
                    <MenuItem key={n} value={String(n)}>{n}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button variant="outlined" onClick={() => { setGroupsManageOpen(true); }} disabled={groupsLoading || subsLoading} startIcon={<CampaignIcon />}>Manage groups</Button>
              <Button variant="outlined" onClick={loadGroups} disabled={groupsLoading || subsLoading} startIcon={groupsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}>Refresh</Button>
              <Button variant="outlined" onClick={fetchAllSubscribers} disabled={groupsLoading || subsLoading} startIcon={subsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}>Fetch</Button>
              <Button variant="contained" onClick={openAdd} disabled={groupsLoading || subsLoading} startIcon={<AddIcon />}>Add subscriber</Button>
            </Box>
          </Box>

          <Divider />

          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 160px 160px 160px' }, gap: 1.5, mb: 2 }}>
              <TextField
                size="small"
                label="Search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Email or name"
              />
              <FormControl size="small">
                <InputLabel id="ml-status">Status</InputLabel>
                <Select
                  labelId="ml-status"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(String(e.target.value))}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="unsubscribed">Unsubscribed</MenuItem>
                  <MenuItem value="bounced">Bounced</MenuItem>
                  <MenuItem value="junk">Junk</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" label="From" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" label="To" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Box>

            <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Subscribed</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSubscribers.map((s, idx) => (
                    <TableRow key={s.id || `${s.email}_${idx}`} hover>
                      <TableCell sx={{ fontWeight: 800 }}>{s.email}</TableCell>
                      <TableCell>{s.status || '-'}</TableCell>
                      <TableCell>{String((s.fields?.name || '') + ' ' + (s.fields?.last_name || '')).trim() || '-'}</TableCell>
                      <TableCell>{s.subscribed_at || '-'}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(s)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => openDelete(s)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredSubscribers.length === 0 && !subsLoading && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary">No subscribers match your filters</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {subsLoading && (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={18} />
                          <Typography variant="body2" color="text.secondary">Loading subscribers...</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {nextCursor && (
              <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                <Button variant="text" onClick={() => loadSubscribers(nextCursor)} disabled={subsLoading}>
                  Load more
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      );
    }

    return (
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CampaignIcon fontSize="small" />
            <Typography fontWeight={900}>Campaign Drafts</Typography>
            {campaignsSaving && <CircularProgress size={16} />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={loadCampaignsList} disabled={campaignsLoading || campaignsSaving} startIcon={<RefreshIcon />}>Reload</Button>
            <Button variant="contained" onClick={createNewDraft} disabled={campaignsLoading || campaignsSaving || !state.selectedId} startIcon={<AddIcon />}>New draft</Button>
          </Box>
        </Box>
        <Divider />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '360px 1fr' },
            minHeight: 460,
          }}
        >
          <Box sx={{ borderRight: { md: '1px solid' }, borderColor: { md: 'divider' } }}>
            <Box sx={{ position: 'relative' }}>
              {campaignsLoading && (
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}>
                  <LinearProgress />
                </Box>
              )}
              <List dense disablePadding sx={{ maxHeight: { xs: 260, md: 460 }, overflow: 'auto' }}>
                {campaignDrafts.length === 0 && !campaignsLoading && (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">No drafts yet</Typography>
                  </Box>
                )}
                {campaignDrafts.map((d) => (
                  <ListItemButton
                    key={d.id}
                    selected={String(d.id) === String(campaignSelectedId)}
                    onClick={() => setCampaignSelectedId(String(d.id))}
                    sx={{ alignItems: 'flex-start', py: 1.1 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                          <Typography fontWeight={900} fontSize={13} noWrap>{d.name || 'Draft'}</Typography>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteDraft(String(d.id)); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {d.subject ? `Subject: ${d.subject}` : 'No subject yet'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {fmtWhen(d.updatedAt || d.createdAt) || ''}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Box>

          <Box sx={{ p: 2 }}>
            {!campaignDraft ? (
              <Box sx={{ p: 2 }}>
                <Typography fontWeight={900} gutterBottom>Select a draft</Typography>
                <Typography variant="body2" color="text.secondary">Create a new draft or select one from the left.</Typography>
              </Box>
            ) : (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Box>
                    <Typography fontWeight={900} fontSize={16}>{campaignDraft.name || 'Draft'}</Typography>
                    <Typography variant="caption" color="text.secondary">Autosaves to <code>storage/mail-campaigns</code></Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => campaignDraft && saveCampaignDraft(campaignDraft)}
                      disabled={campaignsSaving || campaignPublishing || campaignSending}
                      startIcon={campaignsSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                    >
                      Save now
                    </Button>
                    <Button
                      variant="contained"
                      onClick={publishCampaignDraft}
                      disabled={campaignPublishing || campaignsSaving || campaignSending}
                      startIcon={campaignPublishing ? <CircularProgress size={16} /> : <CampaignIcon />}
                    >
                      Publish to MailerLite
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={openSendDialog}
                      disabled={campaignSending || campaignsSaving || campaignPublishing || !campaignDraft.mailerliteCampaignId}
                      startIcon={campaignSending ? <CircularProgress size={16} /> : <PeopleIcon />}
                    >
                      Send
                    </Button>
                    {campaignFieldErrors.publish && (
                      <Typography variant="caption" color="error" sx={{ maxWidth: 520 }}>
                        {campaignFieldErrors.publish}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Draft name"
                    value={campaignDraft.name}
                    onChange={(e) => updateCampaignDraft({ name: e.target.value })}
                    fullWidth
                    error={Boolean(campaignFieldErrors.name)}
                    helperText={campaignFieldErrors.name || undefined}
                  />
                  <TextField
                    label="Subject"
                    value={campaignDraft.subject}
                    onChange={(e) => updateCampaignDraft({ subject: e.target.value })}
                    fullWidth
                    error={Boolean(campaignFieldErrors.subject)}
                    helperText={campaignFieldErrors.subject || undefined}
                  />
                  <TextField
                    label="From email"
                    value={campaignDraft.fromEmail}
                    onChange={(e) => updateCampaignDraft({ fromEmail: e.target.value })}
                    fullWidth
                    error={Boolean(campaignFieldErrors.fromEmail)}
                    helperText={campaignFieldErrors.fromEmail || undefined}
                  />
                  <TextField
                    label="From name"
                    value={campaignDraft.fromName}
                    onChange={(e) => updateCampaignDraft({ fromName: e.target.value })}
                    fullWidth
                    error={Boolean(campaignFieldErrors.fromName)}
                    helperText={campaignFieldErrors.fromName || undefined}
                  />
                  <TextField
                    label="Reply-to email"
                    value={campaignDraft.replyToEmail}
                    onChange={(e) => updateCampaignDraft({ replyToEmail: e.target.value })}
                    fullWidth
                    error={Boolean(campaignFieldErrors.replyToEmail)}
                    helperText={campaignFieldErrors.replyToEmail || undefined}
                  />
                  <FormControl fullWidth error={Boolean(campaignFieldErrors.groupId)}>
                    <InputLabel id="ml-campaign-group">Target group</InputLabel>
                    <Select
                      labelId="ml-campaign-group"
                      label="Target group"
                      value={campaignDraft.groupId || ''}
                      onChange={(e) => updateCampaignDraft({ groupId: String(e.target.value || '') || undefined })}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {groups.map((g) => (
                        <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                      ))}
                    </Select>
                    {campaignFieldErrors.groupId && (
                      <FormHelperText>{campaignFieldErrors.groupId}</FormHelperText>
                    )}
                  </FormControl>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Tabs
                  value={campaignTab}
                  onChange={(_, v) => setCampaignTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ mb: 1 }}
                >
                  <Tab value="html" label="HTML" />
                  <Tab value="text" label="Text" />
                  <Tab value="preview" label="Preview" />
                </Tabs>

                {campaignTab === 'html' && (
                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    label="HTML"
                    value={campaignDraft.html}
                    onChange={(e) => updateCampaignDraft({ html: e.target.value })}
                    error={Boolean(campaignFieldErrors.html)}
                    helperText={campaignFieldErrors.html || undefined}
                    sx={{ fontFamily: 'monospace' }}
                  />
                )}

                {campaignTab === 'text' && (
                  <TextField
                    fullWidth
                    multiline
                    minRows={10}
                    label="Plain text"
                    value={campaignDraft.text}
                    onChange={(e) => updateCampaignDraft({ text: e.target.value })}
                    error={Boolean(campaignFieldErrors.text)}
                    helperText={campaignFieldErrors.text || undefined}
                  />
                )}

                {campaignTab === 'preview' && (
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'background.default',
                    }}
                  >
                    <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Preview (sandboxed)</Typography>
                      <Chip size="small" label={campaignDraft.subject ? `Subject: ${campaignDraft.subject}` : 'No subject'} />
                    </Box>
                    <Box sx={{ height: 360 }}>
                      <iframe
                        title="Campaign preview"
                        sandbox="allow-same-origin"
                        style={{ width: '100%', height: '100%', border: 0, background: 'white' }}
                        srcDoc={campaignDraft.html || '<div style="font-family:Arial,sans-serif;padding:16px;color:#666">No HTML yet</div>'}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    );
  }, [
    selected,
    showKey,
    groupCreateName,
    groupsLoading,
    groupCreateLoading,
    groupId,
    groupDeleteLoadingId,
    groups,
    subsLoading,
    perPage,
    q,
    statusFilter,
    dateFrom,
    dateTo,
    filteredSubscribers,
    nextCursor,
    campaignsSaving,
    campaignsLoading,
    campaignDrafts,
    campaignSelectedId,
    campaignDraft,
    campaignPublishing,
    campaignSending,
    campaignFieldErrors,
    campaignTab,
    state.selectedId,
    updateSelected,
    setGroupCreateName,
    createGroup,
    loadGroups,
    setGroupId,
    deleteGroup,
    setPerPage,
    setGroupsManageOpen,
    fetchAllSubscribers,
    openAdd,
    setQ,
    setStatusFilter,
    setDateFrom,
    setDateTo,
    openEdit,
    openDelete,
    loadSubscribers,
    loadCampaignsList,
    createNewDraft,
    deleteDraft,
    setCampaignSelectedId,
    saveCampaignDraft,
    publishCampaignDraft,
    openSendDialog,
    updateCampaignDraft,
    setCampaignTab,
  ]);

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} gutterBottom>
              MailerLite
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage multiple MailerLite accounts. API keys are stored server-side in <code>storage/mailerlite.json</code>.
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button startIcon={<RefreshIcon />} variant="outlined" onClick={load} disabled={loading || saving}>
              Reload
            </Button>
            <Button startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />} variant="contained" onClick={save} disabled={loading || saving}>
              Save
            </Button>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'minmax(320px, 360px) 1fr' },
            gap: 2,
            alignItems: 'start',
          }}
        >
          <Box>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography fontWeight={800}>Accounts</Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addAccount}>
                  Add
                </Button>
              </Box>
              <Divider />
              <Box sx={{ position: 'relative' }}>
                {loading && (
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 2 }}>
                    <LinearProgress />
                  </Box>
                )}
                <List dense disablePadding sx={{ maxHeight: 520, overflow: 'auto' }}>
                  {state.accounts.length === 0 && !loading && (
                    <Box sx={{ p: 2 }}>
                      <Typography variant="body2" color="text.secondary">No accounts yet</Typography>
                    </Box>
                  )}
                  {state.accounts.map((a) => (
                    <ListItemButton
                      key={a.id}
                      selected={a.id === state.selectedId}
                      onClick={() => setSelected(a.id)}
                      sx={{ alignItems: 'flex-start', py: 1.1 }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography fontWeight={800}>{a.name}</Typography>
                            {a.isDefault && <Chip size="small" label="Default" color="primary" variant="outlined" />}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {a.apiKey ? `API Key: ${maskKey(a.apiKey)}` : 'No API key set'}
                            </Typography>
                            {(() => {
                              const u = accountUsage[a.id];
                              if (!u) return null;
                              const freeSubsLimit = 500;
                              const freeEmailsMonthlyLimit = 12000;
                              const subsUsed = typeof u.subscribersTotal === 'number' ? u.subscribersTotal : null;
                              const subsLeft = subsUsed === null ? null : Math.max(0, freeSubsLimit - subsUsed);
                              const emailsUsed = typeof u.emailsSent === 'number' ? u.emailsSent : null;
                              const emailsLeft = emailsUsed === null ? null : Math.max(0, freeEmailsMonthlyLimit - emailsUsed);
                              const pct = emailsUsed === null ? 0 : Math.min(100, Math.max(0, (emailsUsed / freeEmailsMonthlyLimit) * 100));
                              return (
                                <Box sx={{ mt: 0.7 }}>
                                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                                    <Chip size="small" variant="outlined" label={`Subs: ${subsUsed ?? '—'} / ${freeSubsLimit} (${subsLeft === null ? '—' : subsLeft} left)`} />
                                    <Chip size="small" variant="outlined" label={`Emails: ${emailsUsed === null ? '—' : emailsUsed.toLocaleString()} / ${freeEmailsMonthlyLimit.toLocaleString()} (${emailsLeft === null ? '—' : emailsLeft.toLocaleString()} left)`} />
                                  </Box>
                                  <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 999, mt: 0.8 }} />
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                                    {u.cycleStart && u.cycleEnd
                                      ? `Cycle: ${new Date(String(u.cycleStart)).toLocaleDateString()} → ${new Date(String(u.cycleEnd)).toLocaleDateString()} • resets in ${u.resetsInDays ?? '—'}d`
                                      : 'Cycle: —'}
                                  </Typography>
                                </Box>
                              );
                            })()}
                          </Box>
                        }
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                        <Tooltip title="Usage">
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); openUsage(a.id); }}>
                            <InsightsIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={a.isDefault ? 'Default' : 'Make default'}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDefault(a.id); }}>
                            {a.isDefault ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); deleteAccount(a.id); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            </Paper>
          </Box>

          <Box>
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              {!selected ? (
                <Box sx={{ p: 2 }}>
                  <Typography fontWeight={800} gutterBottom>
                    Select an account
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create an account on the left, then paste your MailerLite API key here.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                    <Box>
                      <Typography fontWeight={900} fontSize={16}>
                        {selected.name || 'MailerLite'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {selected.isDefault ? 'Default account' : 'Not default'}
                      </Typography>
                    </Box>
                    <Chip icon={<KeyIcon />} label="Workspace" variant="outlined" />
                  </Box>

                  <Paper elevation={0} sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tabs value={rightSection} onChange={onRightSectionChange} variant="fullWidth" sx={{ flex: 1 }}>
                        <Tab value="api" label="API Keys" />
                        <Tab value="groups" label="Groups" />
                        <Tab value="subscribers" label="Subscribers" />
                        <Tab value="campaigns" label="Campaigns" />
                      </Tabs>
                      <Tooltip title="Maximize">
                        <IconButton onClick={() => openWorkspace(rightSection)}>
                          <OpenInFullIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Paper>

                  {renderRightSectionContent(rightSection)}
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      </Paper>

      <Dialog open={workspaceOpen} onClose={() => setWorkspaceOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography fontWeight={900} fontSize={16}>
                {selected?.name || 'MailerLite'} Workspace
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Manage API keys, groups, subscribers and campaigns in a focused view.
              </Typography>
            </Box>
            <Tooltip title="Close">
              <IconButton onClick={() => setWorkspaceOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Paper elevation={0} sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Tabs value={rightSection} onChange={onRightSectionChange} variant="fullWidth">
              <Tab value="api" label="API Keys" />
              <Tab value="groups" label="Groups" />
              <Tab value="subscribers" label="Subscribers" />
              <Tab value="campaigns" label="Campaigns" />
            </Tabs>
          </Paper>

          {renderRightSectionContent(rightSection)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkspaceOpen(false)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add subscriber</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              type="email"
              fullWidth
              sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
            />
            <TextField label="First name" value={addFirstName} onChange={(e) => setAddFirstName(e.target.value)} fullWidth />
            <TextField label="Last name" value={addLastName} onChange={(e) => setAddLastName(e.target.value)} fullWidth />

            <TextField label="Company" value={addCompany} onChange={(e) => setAddCompany(e.target.value)} fullWidth />
            <TextField label="Phone" value={addPhone} onChange={(e) => setAddPhone(e.target.value)} fullWidth />
            <TextField label="City" value={addCity} onChange={(e) => setAddCity(e.target.value)} fullWidth />
            <TextField label="State" value={addState} onChange={(e) => setAddState(e.target.value)} fullWidth />
            <TextField label="Country" value={addCountry} onChange={(e) => setAddCountry(e.target.value)} fullWidth />
            <TextField label="ZIP" value={addZip} onChange={(e) => setAddZip(e.target.value)} fullWidth />

            <FormControl fullWidth sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}>
              <InputLabel id="ml-add-groups">Groups</InputLabel>
              <Select
                labelId="ml-add-groups"
                label="Groups"
                multiple
                value={addGroups}
                onChange={(e) => {
                  const v = e.target.value as any;
                  setAddGroups(Array.isArray(v) ? v.map(String) : []);
                }}
                renderValue={(selectedIds) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selectedIds as string[]).map((id) => {
                      const g = groups.find((x) => x.id === id);
                      return <Chip key={id} label={g?.name || id} size="small" />;
                    })}
                  </Box>
                )}
              >
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    <Checkbox checked={addGroups.includes(g.id)} />
                    <ListItemText primary={g.name} />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select one or more groups to assign this subscriber to.</FormHelperText>
            </FormControl>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Subscriber will be added/updated and assigned to the selected groups.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveSubscriber} disabled={addSaving} startIcon={addSaving ? <CircularProgress size={16} /> : <AddIcon />}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={usageOpen} onClose={() => setUsageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InsightsIcon />
              <Box>
                <Typography fontWeight={900}>Usage</Typography>
                <Typography variant="caption" color="text.secondary">
                  {state.accounts.find((x) => x.id === usageAccountId)?.name || 'MailerLite'}
                </Typography>
              </Box>
            </Box>
            <FormControl size="small" sx={{ minWidth: 140 }} disabled={usageLoading}>
              <InputLabel id="ml-usage-days">Timeframe</InputLabel>
              <Select
                labelId="ml-usage-days"
                label="Timeframe"
                value={String(usageDays)}
                onChange={(e) => setUsageDays(parseInt(String(e.target.value), 10) || 30)}
              >
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
                <MenuItem value="365">Last 365 days</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          {usageLoading && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress />
            </Box>
          )}

          {(() => {
            const freeSubsLimit = 500;
            const freeEmailsMonthlyLimit = 12000;

            const subsUsed = typeof usageData?.subscribersTotal === 'number' ? usageData.subscribersTotal : null;
            const subsLeft = subsUsed === null ? null : Math.max(0, freeSubsLimit - subsUsed);
            const subsPct = subsUsed === null ? 0 : Math.min(100, Math.max(0, (subsUsed / freeSubsLimit) * 100));

            const days = usageData?.days || usageDays;
            const emailsRaw = typeof usageData?.emailsSent === 'number' ? usageData.emailsSent : null;

            const account = state.accounts.find((x) => x.id === usageAccountId);
            const apiCreated = usageData?.accountCreatedAt ? new Date(String(usageData.accountCreatedAt)) : null;
            const localCreated = account?.createdAt ? new Date(String(account.createdAt)) : null;
            const createdDate = apiCreated && !Number.isNaN(apiCreated.getTime())
              ? apiCreated
              : (localCreated && !Number.isNaN(localCreated.getTime()) ? localCreated : null);

            const now = new Date();
            const cycleStart = (() => {
              if (!createdDate) {
                return new Date(now.getFullYear(), now.getMonth(), 1);
              }
              const anchorDay = createdDate.getDate();
              const tentative = new Date(now.getFullYear(), now.getMonth(), anchorDay);
              if (tentative.getMonth() !== now.getMonth()) {
                // anchor day overflowed (e.g. 31st in Feb) => use last day of month
                return new Date(now.getFullYear(), now.getMonth() + 1, 0);
              }
              if (now < tentative) {
                // not reached anchor day this month => cycle started previous month
                const prev = new Date(now.getFullYear(), now.getMonth() - 1, anchorDay);
                if (prev.getMonth() === ((now.getMonth() + 11) % 12)) return prev;
                return new Date(now.getFullYear(), now.getMonth(), 0);
              }
              return tentative;
            })();
            const cycleEnd = (() => {
              if (!createdDate) {
                return new Date(now.getFullYear(), now.getMonth() + 1, 1);
              }
              const anchorDay = createdDate.getDate();
              const next = new Date(cycleStart.getFullYear(), cycleStart.getMonth() + 1, anchorDay);
              if (next.getMonth() !== ((cycleStart.getMonth() + 1) % 12)) {
                return new Date(cycleStart.getFullYear(), cycleStart.getMonth() + 2, 0);
              }
              return next;
            })();

            const cycleLenDays = Math.max(1, Math.ceil((cycleEnd.getTime() - cycleStart.getTime()) / 86400000));
            const resetsInDays = Math.max(0, Math.ceil((cycleEnd.getTime() - now.getTime()) / 86400000));

            const emailsMonthlyEstimate = emailsRaw === null
              ? null
              : (days > 0 ? Math.round((emailsRaw * cycleLenDays) / days) : emailsRaw);

            const emailsUsed = emailsMonthlyEstimate;
            const emailsLeft = emailsUsed === null ? null : Math.max(0, freeEmailsMonthlyLimit - emailsUsed);
            const emailsPct = emailsUsed === null ? 0 : Math.min(100, Math.max(0, (emailsUsed / freeEmailsMonthlyLimit) * 100));

            const emailsLabel = (days === cycleLenDays)
              ? 'Monthly emails'
              : 'Monthly emails (projected)';

            return (
              <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                  <Box>
                    <Typography fontWeight={900}>Free plan limits</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {createdDate
                        ? `Billing cycle anchored to account creation day (${createdDate.toLocaleDateString()}). Resets in ${resetsInDays} day(s).`
                        : 'Billing cycle anchored to the 1st of the month (account creation date not available).'}
                    </Typography>
                  </Box>
                  <Chip label="Free" variant="outlined" />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, mt: 1 }}>
                  <Paper elevation={0} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Account created</Typography>
                    <Typography fontWeight={900} sx={{ mt: 0.3 }}>
                      {createdDate ? createdDate.toLocaleString() : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {usageData?.accountCreatedAt ? 'From MailerLite' : (account?.createdAt ? 'From this app' : '')}
                    </Typography>
                  </Paper>
                  <Paper elevation={0} sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Billing cycle</Typography>
                    <Typography fontWeight={900} sx={{ mt: 0.3 }}>
                      {`${cycleStart.toLocaleDateString()} → ${cycleEnd.toLocaleDateString()}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Resets in {resetsInDays} day(s)</Typography>
                  </Paper>
                </Box>

                <Box sx={{ mt: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
                    <Typography fontWeight={800}>Subscribers</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {subsUsed === null ? '—' : `${subsUsed} / ${freeSubsLimit}`}
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={subsPct} sx={{ height: 10, borderRadius: 999, mt: 0.8 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                    {subsLeft === null ? 'Remaining: —' : `Remaining: ${subsLeft}`}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
                    <Typography fontWeight={800}>{emailsLabel}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {emailsUsed === null ? '—' : `${emailsUsed.toLocaleString()} / ${freeEmailsMonthlyLimit.toLocaleString()}`}
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={emailsPct} sx={{ height: 10, borderRadius: 999, mt: 0.8 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
                    {emailsLeft === null ? 'Remaining: —' : `Remaining: ${emailsLeft.toLocaleString()}`}
                  </Typography>
                </Box>
              </Paper>
            );
          })()}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Subscribers</Typography>
              <Typography variant="h6" fontWeight={900} sx={{ mt: 0.5 }}>
                {usageData?.subscribersTotal ?? '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">Current total</Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Emails sent</Typography>
              <Typography variant="h6" fontWeight={900} sx={{ mt: 0.5 }}>
                {usageData ? usageData.emailsSent : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">In selected timeframe</Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Campaigns sent</Typography>
              <Typography variant="h6" fontWeight={900} sx={{ mt: 0.5 }}>
                {usageData ? usageData.campaignsSent : '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">In selected timeframe</Typography>
            </Paper>

            <Paper elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">Engagement</Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.8, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Opens: ${usageData ? usageData.opens : '—'}`} variant="outlined" />
                <Chip size="small" label={`Clicks: ${usageData ? usageData.clicks : '—'}`} variant="outlined" />
                <Chip size="small" label={`Unsubs: ${usageData ? usageData.unsubscribes : '—'}`} variant="outlined" />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.8, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Hard bounces: ${usageData ? usageData.hardBounces : '—'}`} variant="outlined" />
                <Chip size="small" label={`Soft bounces: ${usageData ? usageData.softBounces : '—'}`} variant="outlined" />
              </Box>
            </Paper>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" fontWeight={900} gutterBottom>Plan & limits</Typography>
            <Typography variant="body2" color="text.secondary">
              MailerLite’s Connect API does not provide your exact plan/quota values. Free tier remaining above is calculated using known Free tier limits (500 subscribers / 12,000 monthly emails) and the selected timeframe.
            </Typography>
            {usageData?.notes?.length ? (
              <Box sx={{ mt: 1 }}>
                {usageData.notes.map((n, idx) => (
                  <Typography key={idx} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {n}
                  </Typography>
                ))}
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit subscriber</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              value={editEmail}
              onChange={(e) => setEditEmail(e.target.value)}
              type="email"
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}
              helperText={editId ? `ID: ${editId}` : undefined}
            />
            <TextField label="First name" value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} fullWidth />
            <TextField label="Last name" value={editLastName} onChange={(e) => setEditLastName(e.target.value)} fullWidth />

            <TextField label="Company" value={editCompany} onChange={(e) => setEditCompany(e.target.value)} fullWidth />
            <TextField label="Phone" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} fullWidth />
            <TextField label="City" value={editCity} onChange={(e) => setEditCity(e.target.value)} fullWidth />
            <TextField label="State" value={editState} onChange={(e) => setEditState(e.target.value)} fullWidth />
            <TextField label="Country" value={editCountry} onChange={(e) => setEditCountry(e.target.value)} fullWidth />
            <TextField label="ZIP" value={editZip} onChange={(e) => setEditZip(e.target.value)} fullWidth />

            <FormControl fullWidth sx={{ gridColumn: { xs: 'auto', md: '1 / -1' } }}>
              <InputLabel id="ml-edit-groups">Groups</InputLabel>
              <Select
                labelId="ml-edit-groups"
                label="Groups"
                multiple
                value={editGroups}
                onChange={(e) => {
                  const v = e.target.value as any;
                  setEditGroups(Array.isArray(v) ? v.map(String) : []);
                }}
                renderValue={(selectedIds) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selectedIds as string[]).map((id) => {
                      const g = groups.find((x) => x.id === id);
                      return <Chip key={id} label={g?.name || id} size="small" />;
                    })}
                  </Box>
                )}
              >
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    <Checkbox checked={editGroups.includes(g.id)} />
                    <ListItemText primary={g.name} />
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>Select one or more groups to assign this subscriber to.</FormHelperText>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={saveSubscriberEdit} disabled={editSaving} startIcon={editSaving ? <CircularProgress size={16} /> : <SaveIcon />}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={groupsManageOpen} onClose={() => setGroupsManageOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Groups</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 1.5, alignItems: 'start' }}>
            <TextField
              label="New group name"
              value={groupCreateName}
              onChange={(e) => setGroupCreateName(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              onClick={createGroup}
              disabled={groupCreateLoading}
              startIcon={groupCreateLoading ? <CircularProgress size={16} /> : <AddIcon />}
              sx={{ height: 56 }}
            >
              Create
            </Button>
          </Box>

          <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
            <List dense disablePadding>
              {groups.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">No groups</Typography>
                </Box>
              )}
              {groups.map((g) => (
                <ListItemButton
                  key={g.id}
                  selected={g.id === groupId}
                  onClick={() => setGroupId(g.id)}
                  sx={{ py: 1.1 }}
                >
                  <ListItemText
                    primary={<Typography fontWeight={900} fontSize={13}>{g.name}</Typography>}
                    secondary={<Typography variant="caption" color="text.secondary">{g.id}</Typography>}
                  />
                  <Tooltip title="Delete group">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => { e.stopPropagation(); deleteGroup(g.id); }}
                        disabled={Boolean(groupDeleteLoadingId) || groupDeleteLoadingId === g.id}
                      >
                        {groupDeleteLoadingId === g.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </span>
                  </Tooltip>
                </ListItemButton>
              ))}
            </List>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Groups are synced with MailerLite. Deleting a group removes it from your MailerLite account.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupsManageOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete subscriber?</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            This will remove the subscriber from your MailerLite account.
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            <Chip label={deleteEmail || deleteIdOrEmail} color="error" variant="outlined" />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteSubscriber} disabled={deleteLoading} startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={sendSuccessOpen}
        onClose={() => setSendSuccessOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Campaign queued</DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 999,
                bgcolor: 'success.light',
                color: 'success.dark',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '0 0 auto',
              }}
            >
              <CheckCircleIcon />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.2 }}>
                Your campaign was sent to all recipients
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                It can take a few minutes for MailerLite to deliver the email to everyone.
              </Typography>

              {sendSuccessWhenText ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Scheduled time
                  </Typography>
                  <Chip label={sendSuccessWhenText} variant="outlined" />
                </Box>
              ) : (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Tip
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    If you don’t see it immediately, refresh MailerLite in a moment — the status will update shortly.
                  </Typography>
                </Box>
              )}

              {sendSuccessInfo?.campaignId ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Campaign ID
                  </Typography>
                  <Chip label={sendSuccessInfo.campaignId} size="small" variant="outlined" />
                </Box>
              ) : null}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => setSendSuccessOpen(false)}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sendOpen} onClose={() => setSendOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Send campaign</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="ml-send-target">Target</InputLabel>
              <Select
                labelId="ml-send-target"
                label="Target"
                value={sendTarget}
                onChange={(e) => {
                  const v = String(e.target.value) as any;
                  setSendTarget(v);
                  setSendSelectedEmails([]);
                }}
              >
                <MenuItem value="draft_group">Draft target group</MenuItem>
                <MenuItem value="selected_subscribers">Selected subscribers</MenuItem>
              </Select>
            </FormControl>

            <Box>
              {sendTarget === 'draft_group' ? (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    This will send to the draft's selected "Target group".
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={campaignDraft?.groupId ? `Group ID: ${campaignDraft.groupId}` : 'No group selected'}
                      color={campaignDraft?.groupId ? 'default' : 'warning'}
                      variant="outlined"
                    />
                  </Box>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Select recipients from the currently loaded subscriber list. We'll create a temporary MailerLite group behind the scenes.
                  </Typography>
                  <Box sx={{ maxHeight: 260, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox"></TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Name</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSubscribers.slice(0, 200).map((s, idx) => {
                          const email = s.email;
                          const checked = sendSelectedEmails.includes(email);
                          const name = String((s.fields?.name || '') + ' ' + (s.fields?.last_name || '')).trim() || '-';
                          return (
                            <TableRow key={s.id || `${email}_${idx}`} hover>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={checked}
                                  onChange={(e) => {
                                    const on = e.target.checked;
                                    setSendSelectedEmails((prev) => {
                                      if (on) return prev.includes(email) ? prev : [...prev, email];
                                      return prev.filter((x) => x !== email);
                                    });
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>{email}</TableCell>
                              <TableCell>{name}</TableCell>
                            </TableRow>
                          );
                        })}
                        {filteredSubscribers.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3}>
                              <Typography variant="body2" color="text.secondary">No subscribers loaded</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Chip label={`Selected: ${sendSelectedEmails.length}`} variant="outlined" />
                    {filteredSubscribers.length > 200 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Showing first 200 results. Use search/filters above to narrow down.
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendOpen(false)} disabled={campaignSending}>Cancel</Button>
          <Button variant="contained" onClick={sendCampaign} disabled={campaignSending} startIcon={campaignSending ? <CircularProgress size={16} /> : <PeopleIcon />}>Send now</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MailerLiteTab;
